import { ThemeEvents } from '@theme/events';

const TOAST_ID = 'cart-action-toast';
const TOAST_DISPLAY_DURATION_MS = 2600;

/** @type {number | undefined} */
let hideTimeoutId;
/** @type {number | undefined} */
let showAnimationFrameId;

/**
 * @param {string | undefined | null} value
 * @returns {string}
 */
function normalizeText(value) {
  if (!value) return '';
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * @param {unknown} target
 * @returns {HTMLElement | null}
 */
function getProductFormFromTarget(target) {
  if (!(target instanceof Element)) return null;
  if (target.matches('product-form-component')) return /** @type {HTMLElement} */ (target);
  return target.closest('product-form-component');
}

/**
 * @param {Record<string, any>} data
 * @returns {HTMLElement | null}
 */
function getProductFormFromData(data) {
  const variantId = data?.variantId ? String(data.variantId) : '';
  if (variantId) {
    const byVariant = document.querySelector(
      `product-form-component input[name="id"][value="${variantId}"]`
    );
    if (byVariant) return byVariant.closest('product-form-component');
  }

  const productId = data?.productId ? String(data.productId) : '';
  if (productId) {
    return document.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }

  return null;
}

/**
 * @param {HTMLElement | null} productForm
 * @returns {string}
 */
function getTitleFromProductForm(productForm) {
  if (!(productForm instanceof HTMLElement)) return '';

  const titleFromData = normalizeText(productForm.dataset.productTitle);
  if (titleFromData) return titleFromData;

  const titleNode = productForm
    .closest('.resource-card, .product-grid__card, product-card')
    ?.querySelector("[ref='productTitleLink'], .resource-card__title");
  return normalizeText(titleNode?.textContent);
}

/**
 * @param {Event} event
 * @param {Record<string, any>} data
 * @returns {string}
 */
function resolveProductTitle(event, data) {
  const titleFromData = normalizeText(data?.productTitle);
  if (titleFromData) return titleFromData;

  const productForm = getProductFormFromTarget(event.target) || getProductFormFromData(data);
  return getTitleFromProductForm(productForm);
}

/**
 * @returns {{toast: HTMLElement, message: HTMLElement}}
 */
function getOrCreateToastElements() {
  const wrapper = document.getElementById(TOAST_ID);
  const toast = wrapper?.firstElementChild;
  const message = wrapper?.querySelector('[data-cart-toast-message]');

  if (toast instanceof HTMLElement && message instanceof HTMLElement) {
    return {
      toast,
      message,
    };
  }

  const toastWrapper = document.createElement('div');
  toastWrapper.id = TOAST_ID;
  toastWrapper.setAttribute('aria-live', 'polite');
  toastWrapper.setAttribute('aria-atomic', 'true');
  toastWrapper.setAttribute('role', 'status');
  toastWrapper.className = 'fixed top-4 z-10 pointer-events-none w-full px-4';
  toastWrapper.style.zIndex = '9999';

  const panel = document.createElement('div');
  panel.className =
    'mx-auto w-full rounded-xs border border-coal-400 bg-navy-700 text-coal-100 px-4 py-3 shadow-md transition-all duration-300 ease-in-out flex items-center gap-2 text-xs font-semibold';
  panel.style.maxWidth = '32rem';
  panel.style.opacity = '0';
  panel.style.transform = 'translateY(-8px)';

  const icon = document.createElement('span');
  icon.className = 'font-bold';
  icon.textContent = '✓';

  const text = document.createElement('span');
  text.dataset.cartToastMessage = '';

  panel.append(icon, text);
  toastWrapper.append(panel);
  document.body.append(toastWrapper);

  return { toast: panel, message: text };
}

/**
 * @param {string} text
 */
function showToast(text) {
  const { toast, message } = getOrCreateToastElements();

  if (showAnimationFrameId) cancelAnimationFrame(showAnimationFrameId);
  if (hideTimeoutId) clearTimeout(hideTimeoutId);

  message.textContent = text;
  toast.style.opacity = '0';
  toast.style.transform = 'translateY(-8px)';

  showAnimationFrameId = requestAnimationFrame(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateY(0)';
  });

  hideTimeoutId = window.setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(-8px)';
  }, TOAST_DISPLAY_DURATION_MS);
}

/**
 * @param {Event} event
 * @returns {string}
 */
function getToastMessage(event) {
  const data = event?.detail?.data ?? {};
  const source = data?.source;

  if (data?.didError) return '';

  if (source === 'product-form-component' && data?.noCartDrawer === true) {
    const title = resolveProductTitle(event, data);
    return title ? `${title} added to cart` : 'Product added to cart';
  }

  if (source === 'product-card-cart-toggle' && data?.changeType === 'increase') {
    const title = resolveProductTitle(event, data);
    const quantity = Number(data?.quantity);
    const quantityLabel = Number.isFinite(quantity) ? ` (${quantity})` : '';
    return title ? `${title} quantity updated${quantityLabel}` : `Product quantity updated${quantityLabel}`;
  }

  return '';
}

/**
 * @param {Event} event
 */
function handleCartUpdate(event) {
  const message = getToastMessage(event);
  if (!message) return;
  showToast(message);
}

document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
